/**
 * DamageSyncSystem — single subscriber to the server's two damage
 * broadcasts. Normalizes both into a unified DamageEvent shape and
 * re-emits on EVENTS.DAMAGE_APPLIED so downstream consumers
 * (CombatManager, DamagePresentationService, CombatLogService) only
 * subscribe to ONE channel with a typed payload.
 *
 * Phase C.1 (2026-05-12) — replaces three independent listeners on
 * EVENTS.PLAYER_DAMAGED + EVENTS.COMBAT_NPC_DAMAGED that each cast
 * the raw broadcast to `any`. A field rename on the server would have
 * broken all three silently; now the conversion lives in
 * src/shared/network/DamageEvent.ts and runs once per broadcast.
 *
 * Lifecycle: start() once at boot, returns an `Unsubscribe` for tests
 * + scene teardown. Idempotent — calling start() twice is a no-op
 * (latches an internal flag to avoid duplicate forwarding).
 *
 * Does NOT replace ancillary listeners with niche purposes
 * (TargetingSystem auto-retarget, RemotePlayerVfxBridge, GameAudioSystem
 * sound triggers, VFXSystem `net:message:` reads). Those keep their
 * direct EVENTS.* subscriptions because they don't need the unified
 * shape and their drift risk is small.
 */

import { EventBus, EVENTS, type Unsubscribe } from '../../../shared/EventBus';
import { fromPlayerDamaged, fromNpcDamaged, type DamageEvent } from '../../../shared/network/DamageEvent';
import type { PlayerDamagedEvent, NpcDamagedEvent } from '../../../shared/types/NetworkMessages';
import { gameLogger } from '../../../utils/GameLogger';

let started = false;
let stopFns: Unsubscribe[] = [];

export const DamageSyncSystem = {
  /**
   * Begin forwarding raw EVENTS.PLAYER_DAMAGED + EVENTS.COMBAT_NPC_DAMAGED
   * into the unified EVENTS.DAMAGE_APPLIED stream. Returns the stop
   * function so callers (tests, scene teardown) can cleanly unsubscribe.
   *
   * Idempotent — repeat calls return the same stop function.
   */
  start(): Unsubscribe {
    if (started) return DamageSyncSystem.stop;
    started = true;
    const bus = EventBus.getInstance();

    const offPlayer = bus.on(EVENTS.PLAYER_DAMAGED, (raw: PlayerDamagedEvent) => {
      try {
        const ev: DamageEvent = fromPlayerDamaged(raw);
        bus.emit(EVENTS.DAMAGE_APPLIED, ev);
      } catch (err) {
        gameLogger.warn('combat', '[DamageSyncSystem] failed to normalize playerDamaged', err);
      }
    });

    const offNpc = bus.on(EVENTS.COMBAT_NPC_DAMAGED, (raw: NpcDamagedEvent) => {
      try {
        const ev: DamageEvent = fromNpcDamaged(raw);
        bus.emit(EVENTS.DAMAGE_APPLIED, ev);
      } catch (err) {
        gameLogger.warn('combat', '[DamageSyncSystem] failed to normalize npcDamaged', err);
      }
    });

    stopFns = [offPlayer, offNpc];
    gameLogger.info('combat', '[DamageSyncSystem] forwarding playerDamaged + npcDamaged → DAMAGE_APPLIED');
    return DamageSyncSystem.stop;
  },

  /** Tear down both listeners. Safe to call multiple times. */
  stop(): void {
    for (const fn of stopFns) {
      try { fn(); } catch { /* ignore */ }
    }
    stopFns = [];
    started = false;
  },

  /** Test-only — exposes the started flag so suites can assert idempotence. */
  __isStartedForTest(): boolean {
    return started;
  },
};
